import type { VtkModules } from './loadVtkModules';
import { createThemeFromTokens } from '@fast-computing/fast-graphics/mui-theme';

export type VtkScene = {
  renderWindow: unknown;
  renderer: unknown;
  openGLRenderWindow: unknown;
  interactor: unknown;
  mapper: unknown;
  actor: unknown;
  polydata: unknown;
  scalarBarActor: unknown | null;
  destroy: () => void;
  resize: () => void;
};

const selectedTheme = createThemeFromTokens('atlas', {
  withComponentDefaults: true,
});

function hexToVtkColor(hex: string): [number, number, number] {
  const cleanHex = hex.replace('#', '');

  const r = parseInt(cleanHex.substring(0, 2), 16) / 255;
  const g = parseInt(cleanHex.substring(2, 4), 16) / 255;
  const b = parseInt(cleanHex.substring(4, 6), 16) / 255;

  return [r, g, b];
}

export function createVtkScene(
  vtk: VtkModules,
  container: HTMLDivElement,
): VtkScene {
  /* eslint-disable @typescript-eslint/no-explicit-any */
  const [r, g, b] = hexToVtkColor(selectedTheme.palette.background.default);
  const renderWindow = (vtk.vtkRenderWindow as any).newInstance();
  const renderer = (vtk.vtkRenderer as any).newInstance({
    background: [r, g, b],
  });
  renderWindow.addRenderer(renderer);

  const openGLRenderWindow = (vtk.vtkOpenGLRenderWindow as any).newInstance();
  openGLRenderWindow.setContainer(container);
  renderWindow.addView(openGLRenderWindow);

  const interactor = (vtk.vtkInteractor as any).newInstance();
  interactor.setView(openGLRenderWindow);
  interactor.initialize();
  interactor.bindEvents(container);
  interactor.setInteractorStyle((vtk.vtkInteractorStyle as any).newInstance());

  const mapper = (vtk.vtkMapper as any).newInstance();
  const actor = (vtk.vtkActor as any).newInstance();
  const polydata = (vtk.vtkPolyData as any).newInstance();

  mapper.setInputData(polydata);
  actor.setMapper(mapper);
  actor.getProperty().setInterpolationToGouraud();
  renderer.addActor(actor);

  const resize = () => {
    const rect = container.getBoundingClientRect();
    openGLRenderWindow.setSize(
      Math.max(1, Math.floor(rect.width)),
      Math.max(1, Math.floor(rect.height)),
    );
    renderWindow.render();
  };

  resize();

  return {
    renderWindow,
    renderer,
    openGLRenderWindow,
    interactor,
    mapper,
    actor,
    polydata,
    scalarBarActor: null,
    resize,
    destroy: () => {
      try {
        interactor.unbindEvents();
      } catch (e) {
        // Ignore errors during unbinding
      }

      try {
        [
          actor,
          mapper,
          polydata,
          openGLRenderWindow,
          interactor,
          renderer,
          renderWindow,
        ].forEach((obj) => obj?.delete?.());
      } catch (e) {
        // Ignore errors during cleanup
      }
    },
  };
  /* eslint-enable @typescript-eslint/no-explicit-any */
}
